import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { getUserId } from "../lib/user";
import Sidebar from "../components/Sidebar";
import NotesPanel from "../components/NotesPanel";
import StageRail from "../components/StageRail";
import { StageLoading } from "../components/Loading";
import Stage0 from "../stages/Stage0";
import Stage1 from "../stages/Stage1";
import Stage2 from "../stages/Stage2";
import Stage3 from "../stages/Stage3";
import { fmtTimer, C } from "../lib/theme";
import { renderNotes } from "../lib/markdown";
import { Menu, NotebookPen, Copy, Trophy, Pause, Play } from "lucide-react";
import { toast } from "sonner";

const statusToView = (s) => (["check", "learn", "teach", "prove", "mastered"].includes(s) ? s : "check");

// Stop counting after this long with no input, so a tab left open overnight
// does not record ten hours of "studying".
const IDLE_AFTER_MS = 3 * 60 * 1000;

export default function Session() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [view, setView] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [paused, setPaused] = useState(false); // manual pause — sticky until clicked again
  const [idle, setIdle] = useState(false);     // auto pause — lifts on any input
  const secondsRef = useRef(0);
  const sessionLoadedRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const lastSavedRef = useRef(-1);
  useEffect(() => { secondsRef.current = seconds; }, [seconds]);

  const refresh = useCallback(async () => {
    const s = await api.getSession(id);
    setSession(s);
    // Deliberately NOT resetting `seconds` here: the client is ahead of the
    // server between saves, so re-reading the session mid-stage would yank the
    // timer backwards. The server value is only trusted on first load.
    return s;
  }, [id]);

  useEffect(() => {
    // Block saves until THIS session's time has loaded — otherwise, on a slow
    // backend, the save interval could write the previous session's seconds
    // onto the new session id.
    sessionLoadedRef.current = false;
    (async () => {
      const uid = getUserId();
      const [s, u, list] = await Promise.all([api.getSession(id), api.getUser(uid), api.listSessions(uid)]);
      setSession(s);
      setUser(u);
      setSessions(list);
      setSeconds(s.timeSpentSec || 0);
      lastSavedRef.current = s.timeSpentSec || 0;
      sessionLoadedRef.current = true;
      setView(statusToView(s.status));
    })();
  }, [id]);

  // Anything the user does counts as activity and lifts an idle pause.
  useEffect(() => {
    const mark = () => { lastActivityRef.current = Date.now(); };
    const evs = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    const onVis = () => { if (!document.hidden) mark(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      evs.forEach((e) => window.removeEventListener(e, mark));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Tick only while the session is actually being worked on: not manually
  // paused, not idle, and the tab is visible.
  useEffect(() => {
    const t = setInterval(() => {
      const isIdle = Date.now() - lastActivityRef.current > IDLE_AFTER_MS;
      setIdle(isIdle);
      if (paused || isIdle || document.hidden) return;
      setSeconds((x) => x + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [paused]);

  // Persist on a stable interval via refs. The old version listed `seconds` as
  // a dependency, which tore the 15s interval down every second — it could
  // never fire, so time was almost never saved.
  useEffect(() => {
    const save = () => {
      if (!sessionLoadedRef.current || secondsRef.current === lastSavedRef.current) return;
      lastSavedRef.current = secondsRef.current;
      api.patchTime(id, secondsRef.current).catch(() => {});
    };
    const t = setInterval(save, 15000);
    const onHide = () => { if (document.hidden) save(); };
    document.addEventListener("visibilitychange", onHide);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onHide); save(); };
  }, [id]);

  const reloadSidebar = useCallback(async () => {
    const list = await api.listSessions(getUserId());
    setSessions(list);
    const u = await api.getUser(getUserId());
    setUser(u);
  }, []);

  if (!session || !view) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <StageLoading color={C.blu} title="Loading your session…" />
      </div>
    );
  }

  const onStage0Done = async () => { await refresh(); await reloadSidebar(); setView("learn"); };
  const onStage1Done = () => { api.enterTeach(id).catch(() => {}); setSession((s) => ({ ...s, status: s.status === "learn" ? "teach" : s.status })); setView("teach"); reloadSidebar(); };
  const onStage2Done = () => { setView("prove"); reloadSidebar(); };
  const onReview = async () => { const s = await refresh(); await reloadSidebar(); setView("learn"); };
  const onMastered = async () => { await reloadSidebar(); navigate("/"); };
  const onDeleted = async (deletedId) => {
    await reloadSidebar();
    if (deletedId === id) navigate("/");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      <Sidebar sessions={sessions} user={user} activeId={id} open={sidebarOpen} onClose={() => setSidebarOpen(false)} onDeleted={onDeleted} />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* top bar */}
        <header className="flex items-center gap-3 border-b border-hair px-4 py-3">
          <button onClick={() => setSidebarOpen(true)} className="rounded-md p-1.5 text-muted hover:bg-white/5 lg:hidden" data-testid="open-sidebar-btn">
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="line-clamp-1 font-display text-lg text-ink">{session.topic}</div>
          </div>
          <div className="hidden sm:block"><StageRail status={session.status} view={view} /></div>
          <button
            onClick={() => setPaused((p) => !p)}
            data-testid="session-timer"
            title={paused ? "Resume the session timer" : "Pause the session timer"}
            aria-pressed={paused}
            className="press flex items-center gap-1.5 rounded-md px-2 py-1 font-mono text-xs hover:bg-white/5"
            style={{ color: paused || idle ? C.amb : "var(--muted)" }}
          >
            {paused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
            {fmtTimer(seconds)}
            {(paused || idle) && <span className="text-[9px] uppercase tracking-widest">{paused ? "paused" : "auto-paused"}</span>}
          </button>
          <button onClick={() => setNotesOpen((o) => !o)} className="rounded-md p-1.5 text-muted hover:bg-white/5 hover:text-ink" data-testid="toggle-notes-btn" title="Notes">
            <NotebookPen className="h-5 w-5" />
          </button>
        </header>

        <div className="flex min-h-0 flex-1">
          <main className="flex-1 overflow-y-auto px-4 py-8 sm:px-8" data-testid="stage-content">
            <div className="mb-4 sm:hidden"><StageRail status={session.status} view={view} /></div>
            {view === "check" && <Stage0 session={session} onComplete={onStage0Done} />}
            {view === "learn" && <Stage1 key={session.attempt} session={session} onComplete={onStage1Done} />}
            {view === "teach" && <Stage2 session={session} onComplete={onStage2Done} />}
            {view === "prove" && <Stage3 session={session} onReview={onReview} onMastered={onMastered} />}
            {view === "mastered" && <MasteredView session={session} />}
          </main>

          <NotesPanel sessionId={id} initialNotes={session.notes} open={notesOpen || undefined} onClose={() => setNotesOpen(false)} />
        </div>
      </div>
    </div>
  );
}

function MasteredView({ session }) {
  const card = session.studyCard;
  const copy = () => {
    navigator.clipboard.writeText(`${session.topic}\n\n${card?.summary || ""}\n\nMy notes:\n${card?.notes || ""}`);
    toast.success("Study card copied");
  };
  return (
    <div className="mx-auto max-w-2xl anim-fade-up" data-testid="mastered-view">
      <div className="flex items-center gap-3">
        <Trophy className="h-8 w-8" style={{ color: C.grn }} />
        <h2 className="font-display text-3xl" style={{ color: C.grn }}>Mastered</h2>
      </div>
      <p className="mt-2 text-muted">You can explain {session.topic.replace(/\?$/, "")} clearly now.</p>
      {card && (
        <div className="mt-6 rounded-lg border border-hair bg-surface p-5" style={{ borderTop: `2px solid ${C.grn}` }}>
          <div className="flex items-center justify-between">
            <div className="font-display text-lg text-ink">Study card</div>
            <button onClick={copy} className="flex items-center gap-1.5 rounded-full border border-hair px-3 py-1 text-xs text-muted hover:text-ink"><Copy className="h-3 w-3" /> Copy</button>
          </div>
          <p className="mt-2 text-sm text-ink/90">{card.summary}</p>
          {card.notes?.trim() && (
            <div className="mt-3 border-t border-hair pt-3">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted">Your notes</div>
              <div className="mt-1 text-sm text-ink/80" dangerouslySetInnerHTML={{ __html: renderNotes(card.notes) }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
